import { t } from "@lingui/macro";
import { useRoute } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtomValue, useSetAtom } from "jotai";
import { isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useFragment } from "react-relay";

import { setFiltersFromStorage } from "@/components/AgendaFilters/utils/setFiltersFromStorage";
import {
  facilityToScheduleOption,
  ScheduleOption,
} from "@/components/ScheduleSwitcher";
import { ScheduleSwitcher } from "@/components/ScheduleSwitcher/ScheduleSwitcher";
import { useFacilityStartEndTime } from "@/features/Facility";
import { useFacilityGroupSize } from "@/features/Facility/hooks/useFacilityGroupSize";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { Button } from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  TimeOfDayFilterControl,
  GroupSizeFilterControl,
} from "../../../../components/AgendaFilters";
import { AgendaWaitlist } from "../../../../components/AgendaWaitlist";
import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator/ClubStackNavigator";
import {
  teeSheetListFilterAtom,
  teeSheetGroupSizeFilterAtom,
  teeSheetHoleFilterAtom,
  teeSheetTimesAtom,
  isBookingMultipleTeeTimesAtom,
  setTeeSheetFiltersAtom,
} from "../../atoms";
import {
  TeeSheetHoleCountSelectorControl,
  TeeSheetCourseSelectorControl,
  TeeSheetMultipleBookingToggle,
  TeeSheetViewFilterRadioGroup,
} from "../../components/TeeSheetBasedFilters";
import { HoleCount } from "../../components/TimeSlotList/components/__generated__/TimeSlotListInternalQuery.graphql";
import { TeeSheetFilterType } from "../../types";
import { TeeSheetBasedFiltersModalFragment_facility$key } from "./__generated__/TeeSheetBasedFiltersModalFragment_facility.graphql";
import { TeeSheetBasedFiltersModal_session$key } from "./__generated__/TeeSheetBasedFiltersModal_session.graphql";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  facilityRef: TeeSheetBasedFiltersModalFragment_facility$key | null;
  sessionRef: TeeSheetBasedFiltersModal_session$key | null;
  selectedCourseIds: string[];
  setSelectedCourseIds: (courseIds: string[]) => void;
  showWaitlist: boolean;
  dateOverride?: string;
};

type LocalFilterState = {
  teeSheetListFilter: TeeSheetFilterType;
  teeSheetGroupSizeFilter: number;
  teeSheetHoleFilter: HoleCount | null;
  teeSheetTimes: { timeGTE?: string; timeLTE?: string } | undefined;
  isBookingMultipleTeeTimes: boolean;
  selectedCourseIds: string[];
};

export const TeeSheetBasedFiltersModal = ({
  isOpen,
  onClose,
  facilityRef,
  sessionRef,
  selectedCourseIds,
  setSelectedCourseIds,
  showWaitlist,
  dateOverride,
}: Props) => {
  const session = useFragment(
    TeeSheetBasedFiltersModalSessionFragmentString,
    sessionRef
  );
  const facility = useFragment(
    TeeSheetBasedFiltersModalFragmentString,
    facilityRef
  );

  const facilityStartEndTime = useFacilityStartEndTime(facility ?? null);
  const facilityGroupSizes = useFacilityGroupSize(facility ?? null);

  const teeSheetListFilter = useAtomValue(teeSheetListFilterAtom);

  const teeSheetGroupSizeFilter = useAtomValue(teeSheetGroupSizeFilterAtom);

  const teeSheetHoleFilter = useAtomValue(teeSheetHoleFilterAtom);

  const teeSheetTimes = useAtomValue(teeSheetTimesAtom);

  const isBookingMultipleTeeTimes = useAtomValue(isBookingMultipleTeeTimesAtom);

  const setTeeSheetFilters = useSetAtom(setTeeSheetFiltersAtom);

  const [localFilterState, setLocalFilterState] = useState<LocalFilterState>({
    teeSheetListFilter,
    teeSheetGroupSizeFilter,
    teeSheetHoleFilter,
    teeSheetTimes,
    isBookingMultipleTeeTimes,
    selectedCourseIds,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLocalFilterState({
      teeSheetListFilter,
      teeSheetGroupSizeFilter,
      teeSheetHoleFilter,
      teeSheetTimes,
      isBookingMultipleTeeTimes,
      selectedCourseIds,
    });
  }, [
    isOpen,
    isBookingMultipleTeeTimes,
    selectedCourseIds,
    teeSheetGroupSizeFilter,
    teeSheetHoleFilter,
    teeSheetListFilter,
    teeSheetTimes,
  ]);

  const onSave = useCallback(() => {
    setTeeSheetFilters({
      listFilter: localFilterState.teeSheetListFilter,
      groupSizeFilter: localFilterState.teeSheetGroupSizeFilter,
      holeFilter: localFilterState.teeSheetHoleFilter,
      times: localFilterState.teeSheetTimes,
      isBookingMultipleTeeTimes: localFilterState.isBookingMultipleTeeTimes,
    });

    if (localFilterState.teeSheetGroupSizeFilter) {
      setFiltersFromStorage(
        `x-whoosh-${facility?.id}-group-size-filter`,
        localFilterState.teeSheetGroupSizeFilter.toString()
      );
    }

    if (localFilterState.teeSheetHoleFilter) {
      setFiltersFromStorage(
        `x-whoosh-${facility?.id}-hole-count-filter`,
        localFilterState.teeSheetHoleFilter
      );
    }

    if (localFilterState.teeSheetTimes) {
      if (localFilterState.teeSheetTimes.timeGTE) {
        setFiltersFromStorage(
          `x-whoosh-${facility?.id}-time-of-day-start-time`,
          localFilterState.teeSheetTimes.timeGTE?.toString()
        );
      }
      if (localFilterState.teeSheetTimes.timeLTE) {
        setFiltersFromStorage(
          `x-whoosh-${facility?.id}-time-of-day-end-time`,
          localFilterState.teeSheetTimes.timeLTE?.toString()
        );
      }
    }

    setSelectedCourseIds(localFilterState.selectedCourseIds);
    onClose();
  }, [
    setTeeSheetFilters,
    localFilterState.teeSheetListFilter,
    localFilterState.teeSheetGroupSizeFilter,
    localFilterState.teeSheetHoleFilter,
    localFilterState.teeSheetTimes,
    localFilterState.isBookingMultipleTeeTimes,
    localFilterState.selectedCourseIds,
    setSelectedCourseIds,
    onClose,
    facility?.id,
  ]);

  const onReset = useCallback(() => {
    setLocalFilterState({
      teeSheetListFilter: "All",
      teeSheetGroupSizeFilter: 0,
      teeSheetHoleFilter: null,
      teeSheetTimes: undefined,
      isBookingMultipleTeeTimes: false,
      selectedCourseIds: [],
    });
  }, []);

  const onListFilterChange = useCallback((value: TeeSheetFilterType) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        teeSheetListFilter: value,
      };
    });
  }, []);

  const onGroupSizeChange = useCallback((value: number | undefined) => {
    if (!isNil(value)) {
      setLocalFilterState((prev) => {
        return {
          ...prev,
          teeSheetGroupSizeFilter: value,
        };
      });
    }
  }, []);

  const onHoleFilterChange = useCallback((value: HoleCount | null) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        teeSheetHoleFilter: value,
      };
    });
  }, []);

  const onTimeOfDayChange = useCallback(
    ({ timeLTE, timeGTE }: { timeLTE?: string; timeGTE?: string }) => {
      if (!timeLTE && !timeGTE) {
        setLocalFilterState((prev) => {
          return {
            ...prev,
            teeSheetTimes: undefined,
          };
        });
      } else {
        setLocalFilterState((prev) => {
          return {
            ...prev,
            teeSheetTimes: {
              timeLTE,
              timeGTE,
            },
          };
        });
      }
    },
    [setLocalFilterState]
  );

  const onBookingMultipleTeeTimesChange = useCallback((value: boolean) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        isBookingMultipleTeeTimes: value,
      };
    });
  }, []);

  const onCourseIdsChange = useCallback((value: string[]) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        selectedCourseIds: value,
      };
    });
  }, []);

  const isPrivate = facility?.club?.supportsPrivate;

  const { date: currentlySelectedDate } =
    useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>().params;

  const date = dateOverride || currentlySelectedDate;

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const curDate = new Date();
    const todayString = format(curDate, "yyyy-MM-dd");
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = facilityToScheduleOption(facility ?? null);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      upperTitle={t`Filters`}
      hideFooterPadding
      lockFullHeight
      content={
        <VStack px={6} flex={1} gap={6} testID="tee-sheet-based-filters-modal">
          <ScheduleSwitcher
            selectedOption={selectedScheduleOption}
            sessionRef={session ?? null}
            onChange={onScheduleSwitcherChange}
          />
          <TimeOfDayFilterControl
            controlStartTime={facilityStartEndTime?.startTime}
            controlEndTime={facilityStartEndTime?.endTime}
            onChange={onTimeOfDayChange}
            selectedTimeRange={localFilterState.teeSheetTimes}
          />
          {(isPrivate && teeSheetListFilter === "Available") ||
          localFilterState.teeSheetListFilter === "Available" ||
          !isPrivate ? (
            <GroupSizeFilterControl
              controlMinGroupSize={facilityGroupSizes.min}
              controlMaxGroupSize={facilityGroupSizes.max}
              onChange={onGroupSizeChange}
              selectedGroupSize={localFilterState.teeSheetGroupSizeFilter}
            />
          ) : null}
          {isPrivate ? (
            <TeeSheetViewFilterRadioGroup
              value={localFilterState.teeSheetListFilter}
              onChange={onListFilterChange}
            />
          ) : null}
          <TeeSheetCourseSelectorControl
            facilityRef={facility ?? null}
            onChange={onCourseIdsChange}
            courseIds={localFilterState.selectedCourseIds}
          />
          {/* We only show the hole filter on public clubs */}
          {!isPrivate ? (
            <TeeSheetHoleCountSelectorControl
              onChange={onHoleFilterChange}
              value={localFilterState.teeSheetHoleFilter ?? null}
              facilityRef={facility ?? null}
            />
          ) : null}
          <TeeSheetMultipleBookingToggle
            facilityRef={facility ?? null}
            onChange={onBookingMultipleTeeTimesChange}
            isBookingMultipleTeeTimes={
              localFilterState.isBookingMultipleTeeTimes
            }
          />
          {showWaitlist ? (
            <AgendaWaitlist
              date={date ?? ""}
              facilityId={facility?.id ?? ""}
              courseIds={selectedCourseIds}
            />
          ) : null}
        </VStack>
      }
      footerContent={
        <ScreenFooter>
          <HStack flex={1} justifyContent="space-between">
            <Box alignSelf="flex-start">
              <Button
                onPress={onReset}
                variant="ghost"
                accessibilityLabel={t`Reset button`}
              >{t`Reset`}</Button>
            </Box>
            <Box alignSelf="flex-end">
              <Button
                onPress={onSave}
                accessibilityLabel={t`Save and Close button`}
              >{t`Save and Close`}</Button>
            </Box>
          </HStack>
        </ScreenFooter>
      }
    />
  );
};

export const TeeSheetBasedFiltersModalFragmentString = graphql`
  fragment TeeSheetBasedFiltersModalFragment_facility on Facility {
    id
    club {
      supportsPrivate
    }
    ...useFacilityStartEndTime_facility
    ...useFacilityGroupSize_facility
    ...TeeSheetCourseSelectorControlFragment_facility
    ...TeeSheetMultipleBookingToggleFragment_Facility
    ...AgendaDateSelectorControlFragment_facility
    ...facilityToScheduleOption_facility
    ...TeeSheetHoleCountSelectorControl_facility
  }
`;

export const TeeSheetBasedFiltersModalSessionFragmentString = graphql`
  fragment TeeSheetBasedFiltersModal_session on Session {
    ...ScheduleSwitcher_session
  }
`;
